import { useOutletContext, useParams, Link } from "react-router-dom";
import { ChevronRight, MapPin, KeyRound, Compass, Info, Check, ArrowUp, Car } from "lucide-react";
import SEO from "../components/SEO";
import SearchWidget from "../components/SearchWidget";
import DestinationCard from "../components/DestinationCard";
import Reveal from "../components/Reveal";
import FaqAccordion from "../components/FaqAccordion";
import { t } from "../i18n";
import { countries, resolvePage, pagePath, staticPath, relatedPages, siteUrl, byId, hubs, hubPath } from "../data";
import NotFound from "./NotFound";

const CTA = {
  en: { ready: "Ready to find your car?", button: "Compare available cars" },
  it: { ready: "Pronto a trovare la tua auto?", button: "Confronta le auto disponibili" },
};

export default function DestinationPage() {
  const { lang } = useOutletContext();
  const { slug } = useParams();
  const page = resolvePage(lang, slug);
  const i = t[lang];

  if (!page) return <NotFound />;

  const c = page[lang];
  const countryData = countries[page.country];
  const parent = page.parent ? byId[page.parent] : null;
  const related = relatedPages(page, lang);
  const pathEn = pagePath("en", page);
  const pathIt = pagePath("it", page);
  const cta = CTA[lang];

  const crumbs = [
    { label: i.common.home, to: `/${lang}` },
    {
      label: page.type === "airport" ? i.common.breadcrumbAirports : i.common.breadcrumbDestinations,
      to: staticPath(lang, page.type === "airport" ? "airports" : "carRental"),
    },
    { label: countryData.name[lang], to: null },
    ...(parent ? [{ label: parent.name[lang], to: pagePath(lang, parent) }] : []),
    { label: page.name[lang], to: null },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: crumbs
          .filter((x) => x.to)
          .concat([{ label: page.name[lang] }])
          .map((x, idx) => ({
            "@type": "ListItem",
            position: idx + 1,
            name: x.label,
            ...(x.to ? { item: siteUrl(x.to) } : {}),
          })),
      },
      {
        "@type": "FAQPage",
        mainEntity: c.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "TouristDestination",
        name: page.name[lang],
        description: c.intro,
        url: siteUrl(pagePath(lang, page)),
      },
    ],
  };

  const sections = [
    { icon: KeyRound, title: i.common.pickupTitle, text: c.pickup },
    { icon: Compass, title: i.common.drivingTitle, text: c.driving },
    { icon: Info, title: i.common.localTitle, text: c.local },
    ...(c.extra || []).map((x) => ({ icon: Car, title: x.h, text: x.p })),
  ];

  const pageHub = hubs.sardegna.pageIds?.includes(page.id)
    ? hubs.sardegna
    : hubs.sicilia.pageIds?.includes(page.id)
      ? hubs.sicilia
      : page.country === "italy"
        ? hubs.italia
        : null;

  const scrollToSearch = () =>
    document.getElementById("search")?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <>
      <SEO lang={lang} title={c.metaTitle} description={c.metaDesc} pathEn={pathEn} pathIt={pathIt} jsonLd={jsonLd} />

      {/* Conversion-first header: destination + search immediately visible */}
      <section className="container-site pt-6 md:pt-10" data-testid="destination-header">
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-1.5 text-xs font-medium text-muted-foreground">
            {crumbs.map((crumb, idx) => (
              <li key={idx} className="flex items-center gap-1.5">
                {idx > 0 && <ChevronRight className="h-3 w-3 opacity-50" aria-hidden="true" />}
                {crumb.to ? (
                  <Link to={crumb.to} data-testid={`breadcrumb-${idx}`} className="transition-colors duration-200 hover:text-petrol">
                    {crumb.label}
                  </Link>
                ) : (
                  <span aria-current={idx === crumbs.length - 1 ? "page" : undefined} className={idx === crumbs.length - 1 ? "text-foreground" : ""}>
                    {crumb.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>

        <div className="mt-6 grid gap-8 lg:grid-cols-2 lg:items-start lg:gap-14">
          <div className="order-1">
            <p className="inline-flex items-center gap-2 rounded-full bg-petrol/10 px-3.5 py-1 text-[11px] font-bold uppercase tracking-widest text-petrol">
              <MapPin className="h-3 w-3" aria-hidden="true" />
              {page.type === "airport" ? i.common.breadcrumbAirports : countryData.name[lang]}
            </p>
            <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tighter text-foreground sm:text-4xl lg:text-5xl">
              {c.h1}
            </h1>
          </div>

          {/* Search engine — dominant element, first on mobile */}
          <div className="order-2 lg:order-2" id="search">
            <div className="rounded-3xl bg-white p-4 shadow-2xl ring-1 ring-black/5 sm:p-6" data-testid="destination-widget-card">
              <SearchWidget lang={lang} channel={page.chan || "site"} />
            </div>
            <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-1.5" data-testid="destination-trust-chips">
              {i.hero.points.slice(0, 3).map((p) => (
                <li key={p} className="flex items-center gap-1.5 text-xs font-semibold text-foreground/70">
                  <Check className="h-3.5 w-3.5 text-petrol" aria-hidden="true" />
                  {p}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">{i.common.affiliateNote}</p>
          </div>

          <div className="order-3 lg:col-span-2">
            <p className="max-w-2xl text-sm leading-relaxed text-foreground/80 md:text-base">{c.intro}</p>
          </div>
        </div>
      </section>

      {/* Chapters */}
      <section className="mt-12 bg-white py-12 md:py-16" data-testid="destination-chapters">
        <div className="container-site grid gap-8 md:grid-cols-3 md:gap-10">
          {sections.map((s, idx) => (
            <Reveal key={s.title} delay={idx * 0.06}>
              <div className="border-t-2 border-foreground pt-4">
                <div className="flex items-center justify-between">
                  <s.icon className="h-5 w-5 text-petrol" aria-hidden="true" />
                  <span className="font-display text-3xl font-extrabold tracking-tighter text-petrol/15">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </div>
                <h2 className="mt-3 font-display text-lg font-bold tracking-tight">{s.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="container-site py-12 md:py-16">
        <div className="grid gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <h2 className="font-display text-2xl font-extrabold tracking-tighter sm:text-3xl">{i.common.faqTitle}</h2>
          </Reveal>
          <Reveal className="lg:col-span-3" delay={0.08}>
            <FaqAccordion faqs={c.faqs} testIdPrefix={`${page.id}-faq`} />
          </Reveal>
        </div>
      </section>

      {/* Extra CTA back to search */}
      <section className="container-site pb-4">
        <Reveal>
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-petrol px-6 py-6 text-white md:px-8">
            <p className="font-display text-xl font-bold tracking-tight md:text-2xl">{cta.ready}</p>
            <button
              onClick={scrollToSearch}
              data-testid="destination-cta-search"
              className="inline-flex items-center gap-2 rounded-full bg-ember px-6 py-3 text-sm font-bold text-black transition-colors duration-200 hover:bg-ember-dark hover:text-white"
            >
              <ArrowUp className="h-4 w-4" aria-hidden="true" />
              {cta.button}
            </button>
          </div>
        </Reveal>
      </section>

      {/* Related destinations */}
      <section className="container-site py-12" data-testid="related-section">
        <Reveal className="flex flex-wrap items-center gap-4">
          <h2 className="font-display text-xl font-extrabold tracking-tighter sm:text-2xl">{i.common.relatedTitle}</h2>
          {pageHub && (
            <Link
              to={hubPath(lang, pageHub)}
              data-testid="hub-context-link"
              className="rounded-full border border-petrol/30 bg-white px-4 py-1.5 text-xs font-bold text-petrol transition-colors duration-200 hover:bg-petrol hover:text-white"
            >
              {pageHub[lang].h1} →
            </Link>
          )}
        </Reveal>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {related.map((r, idx) => (
            <Reveal key={r.id} delay={idx * 0.05}>
              <DestinationCard page={r} lang={lang} />
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}
